import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import apiClient from '@/services/axios';
import { useLoading } from '@/composables/useLoading';
import { useI18n } from 'vue-i18n';
import { resolveApiErrorFromError } from '@/utils/resolveApiError';

const DEFAULT_PAGE_SIZE = 20;
const SHOW_USERS_WITHOUT_DEVICES_KEY = 'lec.admin.users.showUsersWithoutDevices';
const SEARCH_QUERY_KEY = 'lec.admin.users.search';

const readShowUsersWithoutDevices = (): boolean => {
  try {
    const stored = window.localStorage.getItem(SHOW_USERS_WITHOUT_DEVICES_KEY);
    // No stored preference yet - default the filter to on.
    if (stored === null) return true;
    return stored === 'true';
  } catch {
    return true;
  }
};

const readSearchQuery = (): string => {
  try {
    return window.sessionStorage.getItem(SEARCH_QUERY_KEY) || '';
  } catch {
    return '';
  }
};

const USER_SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  roleLabel: 'role',
  tenantName: 'tenant',
  status: 'status',
  deviceCount: 'deviceCount',
};

export interface AdminUser {
  id?: string;
  sub?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  fullName?: string;
  roles?: string[] | Record<string, string> | string;
  role?: string;
  enabled?: boolean;
  status?: string;
  tenantName?: string;
  tenant?: { name?: string; '@id'?: string; id?: string } | string | null;
  tenants?: Array<{ name?: string; '@id'?: string; id?: string } | string>;
  tenantMemberships?: Array<{
    tenant?: { name?: string; '@id'?: string; id?: string } | string;
    tenantId?: string;
    tenantName?: string;
    role?: string;
  }>;
  deviceCount?: number;
  enbasDeviceCount?: number;
  lobasDeviceCount?: number;
  /** @deprecated Use enbasDeviceCount */
  enbasCount?: number;
  /** @deprecated Use lobasDeviceCount */
  lobasCount?: number;
  devices?: Array<{ type?: string }>;
  orgAdmin?: boolean;
  tenantAdmin?: boolean;
  tenantOwner?: boolean;
  tenantMember?: boolean;
  technician?: boolean;
  '@id'?: string;
  '@type'?: string;
  [key: string]: unknown;
}

const extractCollection = <T>(data: unknown): T[] => {
  if (data && typeof data === 'object' && 'hydra:member' in (data as Record<string, unknown>)) {
    const member = (data as Record<string, unknown>)['hydra:member'];
    if (Array.isArray(member)) return member as T[];
  }
  if (Array.isArray(data)) return data as T[];
  return [];
};

export interface UseAdminUserManagementOptions {
  /**
   * When true (default), automatically fetches the users list on mount and
   * polls it every 60 seconds. Views that only need per-id actions
   * (e.g. `getUser`, `updateUserName`) should pass `false` to avoid an
   * unnecessary `GET /api/users` collection request.
   */
  autoFetch?: boolean;
}

export const useAdminUserManagement = (
  options: UseAdminUserManagementOptions = {}
) => {
  const { autoFetch = true } = options;

  const isLoadingFetch = ref(false);
  const errorMessage = ref('');

  const users = ref<AdminUser[]>([]);
  const totalElements = ref(0);

  const currentPage = ref(1);
  const elementsPerPage = ref(DEFAULT_PAGE_SIZE);
  const searchQuery = ref(readSearchQuery());
  // undefined = no filter, true = active only, false = inactive only
  const activeFilter = ref<boolean | undefined>(undefined);
  // undefined = no filter, true = users with >=1 device, false = users with no devices
  const withDevicesFilter = ref<boolean | undefined>(undefined);
  const rolesFilter = ref<string[]>([]);
  const tenantsFilter = ref<string[]>([]);
  // UI convenience: when false, hide users without devices (=> withDevices=true).
  // Kept for backward compatibility with the existing toggle.
  const showUsersWithoutDevices = ref(readShowUsersWithoutDevices());

  // PrimeVue DataTable lazy-sort binding: sortOrder is 1 (asc), -1 (desc) or null (unsorted).
  // sortField holds the DataTable column field (e.g. `roleLabel`), which is mapped to the
  // backend `sortBy` key (e.g. `role`) in buildParams.
  const sortField = ref<string | null>(null);
  const sortOrder = ref<number | null>(null);

  const totalPages = computed(() =>
    Math.max(1, Math.ceil(totalElements.value / elementsPerPage.value))
  );

  const { t } = useI18n();
  const { showLoading, hideLoading } = useLoading();

  const handleError = (error: unknown) => {
    errorMessage.value = resolveApiErrorFromError(error, t);
  };

  const buildParams = (): URLSearchParams => {
    const params = new URLSearchParams();

    const search = searchQuery.value.trim();
    if (search) params.append('search', search);

    if (typeof activeFilter.value === 'boolean') {
      params.append('active', String(activeFilter.value));
    }

    const effectiveWithDevices =
      typeof withDevicesFilter.value === 'boolean'
        ? withDevicesFilter.value
        : showUsersWithoutDevices.value === false
          ? true
          : undefined;
    if (typeof effectiveWithDevices === 'boolean') {
      params.append('withDevices', String(effectiveWithDevices));
    }

    for (const role of rolesFilter.value) {
      if (role) params.append('roles[]', role);
    }

    for (const tenantId of tenantsFilter.value) {
      if (tenantId) params.append('tenants[]', tenantId);
    }

    if (sortField.value && sortOrder.value) {
      const backendField = USER_SORT_FIELD_MAP[sortField.value] ?? sortField.value;
      params.append('sortBy', backendField);
      params.append('sortDir', sortOrder.value === -1 ? 'desc' : 'asc');
    }

    params.append('page', String(currentPage.value));
    params.append('itemsPerPage', String(elementsPerPage.value));

    return params;
  };

  const fetchUsers = async (showLoader = true) => {
    if (showLoader) {
      showLoading();
      isLoadingFetch.value = true;
    }

    errorMessage.value = '';

    try {
      const { data } = await apiClient.get('/users', {
        params: buildParams(),
        headers: { Accept: 'application/ld+json' },
      });

      if (data && data['hydra:member']) {
        users.value = data['hydra:member'];
        totalElements.value =
          data['hydra:totalItems'] ?? data['hydra:member'].length;
      } else if (Array.isArray(data)) {
        users.value = data;
        totalElements.value = data.length;
      } else {
        users.value = [];
        totalElements.value = 0;
      }
    } catch (error) {
      handleError(error);
    } finally {
      if (showLoader) {
        hideLoading();
        isLoadingFetch.value = false;
      }
    }
  };

  const getUser = async (id: string): Promise<AdminUser | null> => {
    errorMessage.value = '';
    try {
      const { data } = await apiClient.get(`/users/${id}`);
      return data as AdminUser;
    } catch (error) {
      handleError(error);
      return null;
    }
  };

  const findUserByEmail = async (email: string): Promise<AdminUser | null> => {
    errorMessage.value = '';
    try {
      const { data } = await apiClient.get('/users', {
        params: { search: email },
        headers: { Accept: 'application/ld+json' },
      });
      const matches = extractCollection<AdminUser>(data);
      return (
        matches.find((user) => (user.email ?? '').toLowerCase() === email.toLowerCase()) ??
        matches[0] ??
        null
      );
    } catch (error) {
      handleError(error);
      return null;
    }
  };

  const activateUser = async (id: string): Promise<boolean> => {
    errorMessage.value = '';
    try {
      await apiClient.put(`/users/${id}/activate`);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  const deactivateUser = async (id: string): Promise<boolean> => {
    errorMessage.value = '';
    try {
      await apiClient.put(`/users/${id}/deactivate`);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  const resendUserInvitation = async (id: string): Promise<boolean> => {
    errorMessage.value = '';
    try {
      await apiClient.post(`/users/${id}/resend-invitation`);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  const updateUserName = async (
    id: string,
    payload: { firstName: string; lastName: string }
  ): Promise<boolean> => {
    errorMessage.value = '';
    try {
      await apiClient.put(`/users/${id}/name`, payload);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  const inviteUser = async (payload: {
    email: string;
    firstName: string;
    lastName: string;
  }): Promise<boolean> => {
    errorMessage.value = '';
    try {
      await apiClient.post('/users/invite', payload);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  const deleteUser = async (userSub: string): Promise<boolean> => {
    errorMessage.value = '';
    try {
      await apiClient.delete('/users/delete', { userSub });
      return true;
    } catch (error) {
      handleError(error);
      return false;
    }
  };

  const setSearch = (value: string) => {
    const next = value.trim();
    if (next === searchQuery.value) return;
    searchQuery.value = next;
    try {
      if (next) {
        window.sessionStorage.setItem(SEARCH_QUERY_KEY, next);
      } else {
        window.sessionStorage.removeItem(SEARCH_QUERY_KEY);
      }
    } catch {
      // ignore quota / privacy errors
    }
    currentPage.value = 1;
    fetchUsers(true);
  };

  const setShowUsersWithoutDevices = (value: boolean) => {
    if (value === showUsersWithoutDevices.value) return;
    showUsersWithoutDevices.value = value;
    try {
      window.localStorage.setItem(SHOW_USERS_WITHOUT_DEVICES_KEY, value ? 'true' : 'false');
    } catch {
      // ignore quota / privacy errors
    }
    currentPage.value = 1;
    fetchUsers(true);
  };

  const setActiveFilter = (value: boolean | undefined) => {
    if (value === activeFilter.value) return;
    activeFilter.value = value;
    currentPage.value = 1;
    fetchUsers(true);
  };

  const setWithDevicesFilter = (value: boolean | undefined) => {
    if (value === withDevicesFilter.value) return;
    withDevicesFilter.value = value;
    currentPage.value = 1;
    fetchUsers(true);
  };

  const setRolesFilter = (roles: string[]) => {
    rolesFilter.value = [...roles];
    currentPage.value = 1;
    fetchUsers(true);
  };

  const setTenantsFilter = (tenantIds: string[]) => {
    tenantsFilter.value = [...tenantIds];
    currentPage.value = 1;
    fetchUsers(true);
  };

  const changePage = (page: number) => {
    if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
      currentPage.value = page;
      fetchUsers(true);
    }
  };

  const changeElementsPerPage = (count: number) => {
    if (count === elementsPerPage.value) return;
    elementsPerPage.value = count;
    currentPage.value = 1;
    fetchUsers(true);
  };

  const setSort = (field: string | null, order: number | null) => {
    sortField.value = field;
    sortOrder.value = order;
    currentPage.value = 1;
    fetchUsers(true);
  };

  watch(totalPages, (pages) => {
    if (currentPage.value > pages) {
      currentPage.value = pages;
      fetchUsers(false);
    }
  });

  let intervalId: ReturnType<typeof setInterval> | null = null;

  onMounted(() => {
    if (!autoFetch) return;
    fetchUsers(true);
    intervalId = setInterval(() => {
      fetchUsers(false);
    }, 60000);
  });

  onBeforeUnmount(() => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  });

  return {
    isLoadingFetch,
    errorMessage,
    users,
    currentPage,
    totalPages,
    totalElements,
    elementsPerPage,
    searchQuery,
    showUsersWithoutDevices,
    activeFilter,
    withDevicesFilter,
    rolesFilter,
    tenantsFilter,
    fetchUsers,
    getUser,
    findUserByEmail,
    activateUser,
    deactivateUser,
    resendUserInvitation,
    updateUserName,
    inviteUser,
    deleteUser,
    setSearch,
    setShowUsersWithoutDevices,
    setActiveFilter,
    setWithDevicesFilter,
    setRolesFilter,
    setTenantsFilter,
    changePage,
    changeElementsPerPage,
    sortField,
    sortOrder,
    setSort,
  };
};

export { extractCollection };
